// @PN/inclusao/components/AttachmentsDialog.tsx

"use client";

import React, { useRef, useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import {
  Paperclip,
  Trash2,
  Upload,
  Download,
  Eye,
  TestTube,
} from "lucide-react";
import {
  usePrenotaStore,
  useUploadAnexoStore,
  anexosUploadState,
  type FileUploadProgress,
} from "@PN/inclusao";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  ScrollArea,
  Progress,
  cn,
  Badge,
} from "visuais";
import { TutorialGuide, useTutorial } from "@PN/_nucleo/TutorialGuide";
import { useTestUpload, useDirectUpload } from "./TestUpload";
import { FilePreviewModal } from "./PreviewModal";
import { useFaturasStore } from "../inclusao/_zustand";
import { useAnexoFinanceiro } from "./_tanstack";

interface LocalAnexo {
  item: string;
  nomeArquivo: string;
  isNew?: boolean;
  url?: string;
  doc?: string;
}

// =================================================================
// COMPONENTE: UploadArea
// =================================================================
interface UploadAreaProps {
  onFilesSelected: (files: FileList | File[]) => void;
}

function UploadArea({ onFilesSelected }: UploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDrop={(e) => {
        e.preventDefault();
        onFilesSelected(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      className="w-full h-40 rounded-lg border-2 border-dashed border-primary/30 p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-primary/5 hover:border-primary/50 transition-colors"
    >
      <Upload className="w-8 h-8 mb-3 text-primary" />
      <p className="text-sm font-medium text-foreground/80 text-center mb-1">
        Arraste e solte arquivos aqui
      </p>
      <p className="text-xs text-muted-foreground text-center">
        ou clique para selecionar arquivos
      </p>
      <input
        type="file"
        multiple
        hidden
        ref={inputRef}
        onChange={(e) => {
          if (e.target.files) onFilesSelected(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// =================================================================
// COMPONENTE: AttachmentItem
// =================================================================
interface AttachmentItemProps {
  anexo: LocalAnexo;
  fileProgress?: {
    progress: number;
    status: string;
  };
  onRemove: (item: string) => void;
  onPreview: (anexo: LocalAnexo) => void;
  onDownload: (anexo: LocalAnexo) => void;
}

function AttachmentItem({
  anexo,
  fileProgress,
  onRemove,
  onPreview,
  onDownload,
}: AttachmentItemProps & {
  idMapping: Map<string, string>;
  uploadFiles: Map<string, FileUploadProgress>;
}) {
  const progress = fileProgress?.progress || 0;
  const isUploading = fileProgress?.status === "uploading";
  const isCompleted = fileProgress?.status === "completed";

  // Delegação para o container (possui dados remotos)
  const handleDownload = () => onDownload(anexo);

  // Função para visualizar o arquivo em nova aba
  const handlePreview = () => onPreview(anexo);

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-card p-4 rounded-lg border shadow-sm transition-all",
        isUploading && "border-blue-200 bg-blue-50/30",
        isCompleted && "border-green-200 bg-popover",
        !isUploading && !isCompleted && "border-border hover:border-border/60"
      )}
    >
      <div className="flex-1 flex flex-col gap-3 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <p
              className="text-sm font-semibold truncate text-foreground"
              title={anexo.nomeArquivo}
            >
              {anexo.nomeArquivo}
            </p>
            {anexo.isNew && (
              <Badge
                variant="secondary"
                className="text-xs bg-blue-100 text-blue-700 border-blue-200"
              >
                Novo
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handlePreview}
              className="w-10 h-10 shrink-0 hover:bg-primary/10 hover:text-primary"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDownload}
              className="w-10 h-10 shrink-0 hover:bg-success/10 hover:text-lime-400"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onRemove(anexo.item)}
              className="w-10 h-10 shrink-0 hover:bg-destructive/10 hover:text-destructive"
              disabled={isUploading}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {fileProgress && (
          <div className="flex items-center gap-3">
            <Progress value={progress} className="h-2 flex-1" />
          </div>
        )}
      </div>
    </div>
  );
}

// =================================================================
// COMPONENTE: AttachmentList
// =================================================================
interface AttachmentListProps {
  localAnexos: LocalAnexo[];
  uploadFiles: Map<string, FileUploadProgress>;
  onRemove: (item: string) => void;
  idMapping: Map<string, string>;
  onPreview: (anexo: LocalAnexo) => void;
  onDownload: (anexo: LocalAnexo) => void;
}

function AttachmentList({
  localAnexos,
  uploadFiles,
  onRemove,
  onPreview,
  onDownload,
  idMapping,
}: AttachmentListProps & { idMapping: Map<string, string> }) {
  if (localAnexos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground/50">
        <Paperclip className="w-12 h-12 mb-3 opacity-30" />
        <span className="text-sm">Nenhum anexo adicionado</span>
        <span className="text-xs">
          Arraste arquivos ou clique na área acima
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {localAnexos.map((anexo) => {
        const autoId = idMapping.get(anexo.item);
        const fileProgress = autoId ? uploadFiles.get(autoId) : undefined;

        return (
          <AttachmentItem
            key={anexo.item}
            anexo={anexo}
            fileProgress={fileProgress}
            onRemove={onRemove}
            onPreview={onPreview}
            onDownload={onDownload}
            idMapping={idMapping}
            uploadFiles={uploadFiles}
          />
        );
      })}
    </div>
  );
}
// =================================================================
// COMPONENTE PRINCIPAL: AttachmentsDialog
// =================================================================
export function AttachmentsDialog({ className, tipo, loading }: { className?: string; tipo?: string; loading?: boolean }) {
  const draft = usePrenotaStore((state) => state.draft);
  const uploadFiles =
    usePrenotaStore((state) => state.aux.uploadFiles) || new Map();
  const addFile = usePrenotaStore((state) => state.actions.aux.files.add);
  const removeFile = usePrenotaStore((state) => state.actions.aux.files.remove);
  const updateFileProgress = usePrenotaStore(
    (state) => state.actions.aux.files.updateProgress
  );
  const addAnexo = usePrenotaStore((state) => state.actions.draft.anexos.add);
  const removeAnexo = usePrenotaStore(
    (state) => state.actions.draft.anexos.remove
  );

  const [localAnexos, setLocalAnexos] = useState<LocalAnexo[]>([]);
  const [open, setOpen] = useState(false);
  const [idMapping, setIdMapping] = useState<Map<string, string>>(new Map()); // Mapeia item sequencial -> autoId
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<
    { url: string; name: string; type: string }[]
  >([]);

  // Dados de edição: identificador atual e anexos remotos do UploadThing
  const rec = useFaturasStore((s) => s.rec);
  const faturaZ15 = useFaturasStore((s) => s.faturaZ15);
  
  // Constrói o identificador para buscar anexos (memoizado para evitar loops)
  const identificador = useMemo(() => {
    return faturaZ15 ? {
      filial: faturaZ15.Z15_FILIAL,
      fornecedor: faturaZ15.Z15_FORNEC,
      loja: faturaZ15.Z15_LOJA,
      cod: faturaZ15.Z15_COD,
    } : null;
  }, [faturaZ15]);
  
  const { data: anexosRemotos } = useAnexoFinanceiro(identificador);

  const { markTutorialAsCompleted } = useTutorial();

  // Passos do tutorial
  const tutorialSteps = [
    {
      id: "upload-arquivos",
      title: "Upload de Arquivos",
      description:
        "Arraste e solte arquivos ou clique para selecionar. Você pode adicionar múltiplos arquivos de uma vez.",
      tip: "💡 Dica: Formatos suportados: PDF, DOC, XLS, imagens e outros documentos",
    },
    {
      id: "gerenciar-anexos",
      title: "Gerenciar Anexos",
      description:
        "Visualize, edite ou remova anexos conforme necessário. Você pode baixar arquivos para verificar o conteúdo.",
      tip: "💡 Dica: Mantenha apenas os anexos relevantes para a nota fiscal",
    },
  ];

  // Sincroniza com a store quando abre o dialog
  useEffect(() => {
    if (open) {
      setLocalAnexos(
        draft.anexos.map((a) => ({
          item: a.item,
          nomeArquivo: a.nomeArquivo,
          isNew: false,
          url: (a as any)?.url,
          doc: (a as any)?.doc,
        }))
      );
    }
  }, [open, draft.anexos]);

  // Dentro do useEffect que observa localAnexos, garantir que anexos válidos sejam salvos automaticamente na store de uploads
  useEffect(() => {
    localAnexos.forEach((anexo) => {
      // Só salva se for novo, tiver nomeArquivo, e ainda não estiver na store
      if (
        anexo.isNew &&
        anexo.nomeArquivo &&
        !useUploadAnexoStore
          .getState()
          .uploads.some((u: { seq: string; }) => u.seq === anexo.item)
      ) {
        const file = anexosUploadState.getFileByItem(anexo.item, idMapping);
        if (file) {
          console.log("[UPLOAD STORE] Salvando anexo:", {
            seq: anexo.item,
            file,
            isFile: file instanceof File,
            fileType: typeof file,
            fileName: file.name,
          });
          useUploadAnexoStore.getState().addUpload({
            seq: anexo.item, // string, mantém zeros à esquerda
            doc: "", // será preenchido no envio real
            file,
          });
        }
      }
    });
  }, [localAnexos, idMapping]);

  const processAndAddFilesLocal = (files: FileList | File[]) => {
    const validFiles = Array.from(files).filter((file) => file.size > 0);
    if (validFiles.length === 0) {
      toast.error("Nenhum arquivo válido selecionado");
      return;
    }

    console.log(
      "%c[AttachmentsDialog] Processando arquivos:",
      "color:#8b5cf6;font-weight:bold",
      {
        total: files.length,
        validos: validFiles.length,
        arquivos: validFiles.map((f) => ({ nome: f.name, tamanho: f.size })),
      }
    );

    validFiles.forEach((file, index) => {
      // Gera ID sequencial baseado no número atual de anexos + novos arquivos
      const currentCount =
        draft.anexos.length + localAnexos.filter((a) => a.isNew).length;
      const item = String(currentCount + index + 1).padStart(3, "0"); // 001, 002, 003...

      // Adiciona o arquivo na store (por enquanto usa o ID automático)
      const autoId = addFile(file);

      // Mapeia o ID sequencial com o ID automático
      setIdMapping((prev) => new Map(prev).set(item, autoId));

      const newAnexo: LocalAnexo = {
        item, // Usa o ID sequencial para o anexo
        nomeArquivo: file.name,
        isNew: true,
      };
      setLocalAnexos((prev) => [...prev, newAnexo]);

      // Simula upload (você pode substituir por upload real)
      simulateUpload(autoId); // Usa o ID automático para o progresso
    });

    toast.success(`Adicionados ${validFiles.length} arquivo(s)`);
  };

  const simulateUpload = (item: string) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 20;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
      }
      updateFileProgress(item, progress);
    }, 100);
  };

  const handleRemoveLocal = (item: string) => {
    console.log(
      "%c[AttachmentsDialog] Removendo anexo:",
      "color:#ef4444;font-weight:bold",
      { item }
    );

    // Remove do estado local
    setLocalAnexos((prev) => prev.filter((a) => a.item !== item));

    // Remove o arquivo da store usando o ID automático
    const autoId = idMapping.get(item);
    if (autoId) {
      removeFile(autoId);
      setIdMapping((prev) => {
        const newMap = new Map(prev);
        newMap.delete(item);
        return newMap;
      });
    }
  };

  const guessMimeFromName = (name?: string): string => {
    if (!name) return "application/octet-stream";
    const lower = name.toLowerCase();
    if (lower.endsWith(".pdf")) return "application/pdf";
    if (lower.endsWith(".png")) return "image/png";
    if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
    if (lower.endsWith(".webp")) return "image/webp";
    if (lower.endsWith(".gif")) return "image/gif";
    return "application/octet-stream";
  };

  const isBlobUrl = (url: string) => url.startsWith("blob:");

  const handlePreviewOpen = (anexo: LocalAnexo) => {
    try {
      setPreviewLoading(true);
      const autoId = idMapping.get(anexo.item);
      const localFile = autoId ? uploadFiles.get(autoId)?.file : undefined;

      if (localFile) {
        const blobUrl = URL.createObjectURL(localFile);
        setPreviewFiles([
          { url: blobUrl, name: anexo.nomeArquivo, type: localFile.type || guessMimeFromName(anexo.nomeArquivo) },
        ]);
        setPreviewOpen(true);
        return;
      }

      const docUrl = anexo.doc ? `https://utfs.io/f/${anexo.doc}` : undefined;
      const finalUrl = anexo.url || docUrl;
      if (finalUrl) {
        setPreviewFiles([
          { url: finalUrl, name: anexo.nomeArquivo, type: guessMimeFromName(anexo.nomeArquivo) },
        ]);
        setPreviewOpen(true);
        return;
      }

      // Fallback: utiliza anexos remotos do REC corrente (edição)
      if (anexosRemotos && anexosRemotos.length > 0) {
        const files = anexosRemotos.map((d) => ({
          url: d.fileData.ufsUrl || d.fileData.fileUrl,
          name: d.fileData.fileName,
          type: d.fileData.fileType || guessMimeFromName(d.fileData.fileName),
        }));
        setPreviewFiles(files);
        setPreviewOpen(true);
        return;
      }

      toast.error("Não foi possível localizar o arquivo para visualização");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    // Revoga blob URLs para evitar vazamentos
    previewFiles.forEach((f) => {
      if (isBlobUrl(f.url)) URL.revokeObjectURL(f.url);
    });
    setPreviewOpen(false);
  };

  // Download centralizado para cobrir local, url/doc e UploadThing por REC
  const handleDownloadAnexo = async (anexo: LocalAnexo) => {
    const forceNavigateDownload = (url: string, filename?: string) => {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || anexo.nomeArquivo || "arquivo";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 100);
    };

    const downloadBlob = (blob: Blob, filename?: string) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || anexo.nomeArquivo || "arquivo";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 100);
    };

    // 1) Local blob
    const autoId = idMapping.get(anexo.item);
    const fileObj = autoId ? uploadFiles.get(autoId)?.file : undefined;
    if (fileObj) {
      downloadBlob(fileObj, anexo.nomeArquivo);
      return;
    }

    // 2) URL direta ou doc
    const remoteUrl = anexo.url || (anexo.doc ? `https://utfs.io/f/${anexo.doc}` : undefined);
    if (remoteUrl) {
      try {
        const res = await fetch(remoteUrl, { mode: "cors", credentials: "omit" });
        if (!res.ok) throw new Error("Falha ao baixar");
        const blob = await res.blob();
        downloadBlob(blob, anexo.nomeArquivo);
        return;
      } catch (e) {
        // fallback: navegação direta
        forceNavigateDownload(remoteUrl, anexo.nomeArquivo);
        return;
      }
    }

    // 3) UploadThing por REC
    if (anexosRemotos && anexosRemotos.length > 0) {
      const found = anexosRemotos.find((d) => d.fileData.fileName === anexo.nomeArquivo) || anexosRemotos[0];
      const url = found?.fileData?.ufsUrl || found?.fileData?.fileUrl;
      if (!url) {
        toast.error("Arquivo não encontrado para download");
        return;
      }
      try {
        const res = await fetch(url, { mode: "cors", credentials: "omit" });
        if (!res.ok) throw new Error("Falha ao baixar");
        const blob = await res.blob();
        const filename = anexo.nomeArquivo || found.fileData.fileName;
        downloadBlob(blob, filename);
        return;
      } catch (e) {
        forceNavigateDownload(url, anexo.nomeArquivo || found.fileData.fileName);
        return;
      }
    }

    toast.error("Arquivo não encontrado para download");
  };

  const handleSaveAll = () => {
    console.log(
      "%c[AttachmentsDialog] Salvando anexos:",
      "color:#10b981;font-weight:bold",
      {
        total: localAnexos.length,
        novos: localAnexos.filter((a) => a.isNew).length,
        existentes: localAnexos.filter((a) => !a.isNew).length,
      }
    );

    // Salva todos os anexos na store
    localAnexos.forEach((anexo) => {
      if (anexo.isNew) {
        addAnexo({
          item: anexo.item,
          nomeArquivo: anexo.nomeArquivo,
          observacao: "",
        });
        // Salva também na store de uploads centralizada
        const file = anexosUploadState.getFileByItem(anexo.item, idMapping);
        if (file) {
          useUploadAnexoStore.getState().addUpload({
            seq: String(anexo.item),
            doc: "", // será preenchido no envio real
            file,
            tipo,
          });
        }
      }
    });

    // Remove anexos que foram deletados
    draft.anexos.forEach((anexo) => {
      if (!localAnexos.some((la) => la.item === anexo.item)) {
        removeAnexo(anexo.item);
        const autoId = idMapping.get(anexo.item);
        if (autoId) {
          removeFile(autoId);
        }
        // Remove também da store de uploads centralizada
        useUploadAnexoStore.getState().removeUpload(String(anexo.item));
      }
    });

    toast.success("Anexos salvos com sucesso");
    setOpen(false);
  };

  const handleClearAll = () => {
    console.log(
      "%c[AttachmentsDialog] Limpando todos os anexos:",
      "color:#f59e0b;font-weight:bold"
    );

    setLocalAnexos([]);
    // Remove todos os arquivos da store auxiliar usando os IDs automáticos
    localAnexos.forEach((anexo) => {
      const autoId = idMapping.get(anexo.item);
      if (autoId) {
        removeFile(autoId);
      }
    });
    setIdMapping(new Map());
  };

  const isSaveDisabled = false;
  const hasUnsavedChanges = localAnexos.length > 0;

  // Função para gerar REC aleatório para teste
  const generateRandomRec = () => {
    const randomRec = Math.floor(Math.random() * 90000) + 10000; // 10000-99999
    toast.success(`REC de teste gerado: ${randomRec}`);
    console.log(`🧪 [TESTE] REC gerado: ${randomRec}`);
    return String(randomRec);
  };

  // Função para testar upload com REC aleatório
  const { testUpload } = useTestUpload();
  const { directUpload } = useDirectUpload();

  const handleTestUpload = async (tipo?: string) => {
    if (!localAnexos.length) {
      toast.error("Adicione arquivos antes de testar o upload");
      return;
    }
  
    const testRec = generateRandomRec();
  
    try {
      console.log(`🧪 [TESTE] Iniciando teste de upload com REC: ${testRec}`);
  
      // Busca os arquivos originais da store
      const files = localAnexos.map(anexo => {
        const file = anexosUploadState.getFileByItem(anexo.item, idMapping);
        if (!file) {
          throw new Error(`Arquivo não encontrado para anexo ${anexo.item}`);
        }
        return file;
      });
  
      const results = await testUpload(files);
  
      // Como o uploadFiles só retorna os dados do onUploadComplete, marcamos tudo como sucesso
      const sucessos = results.length;
      const erros = 0;
  
      console.log(`🧪 [TESTE] Resultado do teste:`, { rec: testRec, total: results.length, sucessos, erros });
  
      toast.success(`✅ Teste concluído! ${sucessos} sucesso(s), ${erros} erro(s)`);
  
    } catch (error) {
      console.error(`🧪 [TESTE] Erro no teste:`, error);
      toast.error("Erro ao executar teste de upload");
    }
  };

  // Função para testar upload direto para servidor específico
  const handleDirectUpload = async (tipo?: string) => {
    if (!localAnexos.length) {
      toast.error("Adicione arquivos antes de testar o upload direto");
      return;
    }

    const testRec = generateRandomRec();

    try {
      console.log(`🚀 [DIRETO] Iniciando teste de upload direto com REC: ${testRec}`);

      // Busca os arquivos originais da store
      const files = localAnexos.map(anexo => {
        const file = anexosUploadState.getFileByItem(anexo.item, idMapping);
        if (!file) {
          throw new Error(`Arquivo não encontrado para anexo ${anexo.item}`);
        }
        return file;
      });

      const results = await directUpload(files);

      console.log(`🚀 [DIRETO] Resultado do teste direto:`, { rec: testRec, results });

      toast.success(`✅ Upload direto concluído! Arquivos enviados para 172.16.99.182:3001`);

    } catch (error) {
      console.error(`🚀 [DIRETO] Erro no teste direto:`, error);
      toast.error("Erro ao executar teste de upload direto");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          disabled={!!loading}
          className={cn("flex justify-between w-full relative", className)}
        >
          <span>
            {loading ? "Carregando..." : `Anexos (${draft.anexos.length})`}
          </span>
          <div className="flex items-center gap-2">
            {!loading && hasUnsavedChanges && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {localAnexos.length}
              </Badge>
            )}
            <Paperclip className="h-4 w-4" />
          </div>
        </Button>
      </DialogTrigger>

      <DialogContent className="min-w-2xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <TutorialGuide
              steps={tutorialSteps}
              title="Tutorial: Gerenciamento de Anexos"
              description="Aprenda a adicionar e gerenciar anexos relacionados à nota fiscal"
              onComplete={() => markTutorialAsCompleted("anexos-prenota")}
            />
            <div className="flex items-center gap-2">
              <Paperclip className="h-5 w-5" />
              Gerenciar Anexos
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-xs">
                  {localAnexos.length} arquivo(s) pendente(s)
                </Badge>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Área de upload */}
        <UploadArea onFilesSelected={processAndAddFilesLocal} />

        {/* Lista de anexos */}
        <ScrollArea className="flex-1 p-4 overflow-auto">
          <AttachmentList
            localAnexos={localAnexos}
            uploadFiles={uploadFiles}
            onRemove={handleRemoveLocal}
            onPreview={handlePreviewOpen}
            onDownload={handleDownloadAnexo}
            idMapping={idMapping}
          />
        </ScrollArea>

        {/* Ações */}
        <DialogFooter className="mt-4 justify-between w-full gap-2 pt-4 border-t">
          {/* Botões de Teste */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleTestUpload("financeiro")} disabled={true}>
              <TestTube className="h-4 w-4" />
              Teste UploadThing
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleDirectUpload("financeiro")} 
              disabled={localAnexos.length === 0 || true}
              className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
            >
              <TestTube className="h-4 w-4" />
              Teste Direto
            </Button>
          </div>
          
          <Button
            variant="outline"
            onClick={handleClearAll}
            disabled={localAnexos.length === 0}
            className="flex items-center gap-2"
          >
            Limpar Tudo
          </Button>
          
          <div className="flex gap-2">
            <Button
              onClick={handleSaveAll}
              disabled={isSaveDisabled}
              className="min-w-[120px]"
            >
              {"Salvar Anexos"}
            </Button>
          </div>
        </DialogFooter>
        {/* Preview Modal */}
        <FilePreviewModal
          files={previewFiles}
          isOpen={previewOpen}
          onClose={handlePreviewClose}
          loading={previewLoading}
        />
      </DialogContent>
    </Dialog>
  );
}
